import { PickingPolicyPicklistBatchingSettings } from '@warebee/frontend/data-access-api-graphql';
import { TFunction } from 'i18next';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Icon from '../../components/icons';
import InputNumber from '../../components/inputs/InputNumber';
import SectionOptional from '../../components/layout/SectionOptional';
import TitleSection from '../../components/layout/TitleSection';
import { PolicyCardItem } from '../../components/policies/PolicyCardItem';
import {
  pickingPolicyRule,
  pickingPolicySelectedIdentity,
} from '../../simulation/store/pickingPolicy/pickingPolicy.state';

enum BatchingSettings {
  'DISABLED' = 'DISABLED',
  'ENABLED' = 'ENABLED',
}
export const getGroupByOptions = (t: TFunction<'simulation'>) => [
  {
    id: BatchingSettings.DISABLED,
    title: t`Disabled`,
    icon: Icon.DataOrders,
  },
  {
    id: BatchingSettings.ENABLED,
    title: t`Enabled`,
    icon: Icon.OrderPickingPriority,
  },
];

const PickingPolicyBatchingEditor: React.FC = () => {
  const { t } = useTranslation('simulation');
  const selectedIdentity = useRecoilValue(pickingPolicySelectedIdentity);
  const [rule, updateRule] = useRecoilState(
    pickingPolicyRule(selectedIdentity?.ruleId),
  );

  if (!selectedIdentity || !rule) return null;

  const selectedGroup = rule?.batchingSettings?.enabled
    ? BatchingSettings.ENABLED
    : BatchingSettings.DISABLED;

  const limitBatchEnabled = !_.isNil(rule?.batchingSettings?.maxPicklistCount);

  const defaultBatchingSettings: PickingPolicyPicklistBatchingSettings = {
    enabled: true,
    maxPicklistCount: 10,
  };
  const batchingSettings = rule?.batchingSettings ?? defaultBatchingSettings;

  const onSelectGroup = (groupBy: BatchingSettings) => {
    updateRule({
      ...rule,
      batchingSettings:
        groupBy === BatchingSettings.DISABLED ? undefined : { enabled: true },
    });
  };

  const onChangeBatchSettings = (
    batchSettings: Omit<PickingPolicyPicklistBatchingSettings, '__typename'>,
  ) => {
    updateRule({
      ...rule,
      batchingSettings: {
        ...rule.batchingSettings,
        ...batchSettings,
      },
    });
  };

  return (
    <TitleSection
      dataComponent="PickingPolicyBatchingEditor"
      id={`policy-editor-batching`}
      title={t`Batching Settings`}
      inSidebarView
      hasScreenTitle
      classNameInner="p-2 space-y-2"
    >
      {_.map(getGroupByOptions(t), (option, index) => {
        return (
          <PolicyCardItem
            key={'PolicyCardItem-Batching-' + option.id}
            isActive={option.id === selectedGroup}
            onClick={() => onSelectGroup(option.id)}
            icon={option.icon}
            iconClassName={`w-6 h-6 lg:w-9 lg:h-9 xl:w-12 xl:h-12 ltr:mr-2 rtl:ml-2`}
            title={option.title}
          />
        );
      })}
      <SectionOptional
        id={'batching-editor-max-picklists'}
        title={t`Limit batch size`}
        value={limitBatchEnabled}
        onChange={v =>
          onChangeBatchSettings({
            maxPicklistCount: v
              ? defaultBatchingSettings.maxPicklistCount
              : null,
          })
        }
      >
        <div className="flex flex-col gap-2">
          <InputNumber
            title={t`Max picklists`}
            value={batchingSettings.maxPicklistCount}
            onChange={v =>
              onChangeBatchSettings({
                maxPicklistCount: v,
              })
            }
            range={[2, Number.MAX_SAFE_INTEGER]}
            debounceInterval={500}
          />
        </div>
      </SectionOptional>
    </TitleSection>
  );
};

export default PickingPolicyBatchingEditor;
